import * as Axios from "axios";
import { AxiosInstance, AxiosPromise, AxiosRequestConfig, CancelTokenSource } from "axios";

/**
 * Can't implement all lambdas defined in interface exposed by axios. see https://stackoverflow.com/questions/53009139/typescript-decorator-interface-with-lambda-axiosinstance
 * and https://github.com/axios/axios/issues/1853
 */
// @ts-ignore
export default class CancelableAxiosInstance implements AxiosInstance {
  public static CANCEL_REQUEST_ERROR_MESSAGE = "CANCEL_REQUEST_ERROR_MESSAGE";

  private axiosInstance: AxiosInstance;
  private source: CancelTokenSource;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
    this.source = Axios.default.CancelToken.source();

    this.axiosInstance.interceptors.request.use(
      (config) => {
        config.cancelToken = this.source.token;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  get defaults() {
    return this.axiosInstance.defaults;
  }

  set defaults(defaults) {
    this.axiosInstance.defaults = defaults;
  }

  get interceptors() {
    return this.axiosInstance.interceptors;
  }

  set interceptors(interceptors) {
    this.axiosInstance.interceptors = interceptors;
  }

  cancelRequests() {
    this.source.cancel(CancelableAxiosInstance.CANCEL_REQUEST_ERROR_MESSAGE);
  }

  delete(url: string, config?: AxiosRequestConfig): AxiosPromise {
    return this.axiosInstance.delete(url, config);
  }

  get<T = any>(url: string, config?: AxiosRequestConfig): AxiosPromise<T> {
    return this.axiosInstance.get(url, config);
  }

  head(url: string, config?: AxiosRequestConfig): AxiosPromise {
    return this.axiosInstance.head(url, config);
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): AxiosPromise<T> {
    return this.axiosInstance.patch(url, data, config);
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): AxiosPromise<T> {
    return this.axiosInstance.post(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): AxiosPromise<T> {
    return this.axiosInstance.put(url, data, config);
  }

  request<T = any>(config: AxiosRequestConfig): AxiosPromise<T> {
    return this.axiosInstance.request(config);
  }
}
